// @format
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";

type Series = { key: string; name: string; token: string; data: number[]; total: number };
type Zoom = { from: number; to: number; hidden: number };
type Payload = { labels: string[]; series: Series[]; zoom: Zoom; empty: boolean };
type View = "bar" | "trend" | "funnel";

const LINE_TENSION = 0.35;
const TREND_FILL_ALPHA = 0.16;

function readToken(el: HTMLElement, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

function withAlpha(color: string, alpha: number): string {
  const hex = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!hex) return color;

  const value = parseInt(hex[1], 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

class CampaignOverviewChart {
  private readonly root: HTMLElement;
  private readonly payload: Payload;
  private readonly canvas: HTMLCanvasElement | null;
  private readonly muted = new Set<string>();

  private chart: Chart | null = null;
  private view: View = "bar";
  private zoomed = true;

  constructor(root: HTMLElement) {
    this.root = root;
    this.payload = JSON.parse(root.dataset.chart || "{}") as Payload;
    this.canvas = root.querySelector<HTMLCanvasElement>('canvas[data-role="canvas"]');

    root.addEventListener("click", this.onClick);
    this.draw();
  }

  destroy(): void {
    this.root.removeEventListener("click", this.onClick);
    this.chart?.destroy();
    this.chart = null;
  }

  isDetached(): boolean {
    return !this.root.isConnected;
  }

  private readonly onClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const chip = target.closest<HTMLElement>(".ovw-chip");
    if (chip && this.root.contains(chip)) return this.toggleSeries(chip);

    const viewButton = target.closest<HTMLElement>('[data-role="views"] [data-view]');
    if (viewButton && this.root.contains(viewButton)) return this.setView(viewButton.dataset.view as View);

    const action = target.closest<HTMLElement>("[data-role]")?.dataset.role;
    if (action === "zoom") return this.toggleZoom();
    if (action === "reset") return this.reset();
  };

  private toggleSeries(chip: HTMLElement): void {
    const key = chip.dataset.series;
    if (!key) return;

    const nowMuted = !this.muted.has(key);
    nowMuted ? this.muted.add(key) : this.muted.delete(key);
    chip.classList.toggle("off", nowMuted);
    chip.setAttribute("aria-pressed", String(!nowMuted));

    // Every series muted would leave an empty grid — keep the last one on.
    if (this.muted.size >= this.payload.series.length) return this.reset();

    this.draw();
  }

  private setView(view: View): void {
    if (!view || view === this.view) return;

    this.view = view;
    this.root.dataset.view = view;
    this.root.querySelectorAll<HTMLElement>('[data-role="views"] [data-view]').forEach((button) => {
      button.classList.toggle("active", button.dataset.view === view);
    });

    this.draw();
  }

  private toggleZoom(): void {
    this.zoomed = !this.zoomed;
    this.root.dataset.zoom = this.zoomed ? "auto" : "full";
    this.draw();
  }

  private reset(): void {
    this.muted.clear();
    this.zoomed = true;
    this.root.dataset.zoom = "auto";
    this.root.querySelectorAll<HTMLElement>(".ovw-chip").forEach((chip) => {
      chip.classList.remove("off");
      chip.setAttribute("aria-pressed", "true");
    });

    this.setView("bar");
    this.draw();
  }

  // The funnel is server-rendered markup, so only the plotted views need a canvas.
  private draw(): void {
    this.chart?.destroy();
    this.chart = null;

    if (!this.canvas || this.view === "funnel") return;

    this.chart = new Chart(this.canvas, this.config());
  }

  private window(): { labels: string[]; slice: (data: number[]) => number[] } {
    const { labels, zoom } = this.payload;
    if (!this.zoomed) return { labels, slice: (data) => data };

    const to = zoom.to + 1;
    return { labels: labels.slice(zoom.from, to), slice: (data) => data.slice(zoom.from, to) };
  }

  private config(): ChartConfiguration {
    const trend = this.view === "trend";
    const { labels, slice } = this.window();
    const surface = readToken(this.root, "--surface", "#ffffff");
    const muted = readToken(this.root, "--text-3", "#a39d90");
    const gridline = readToken(this.root, "--border-2", "#f1ede4");

    const datasets = this.payload.series.map((serie): ChartDataset => {
      const color = readToken(this.root, serie.token, muted);

      return {
        label: serie.name,
        data: slice(serie.data),
        hidden: this.muted.has(serie.key),
        borderColor: color,
        backgroundColor: trend ? withAlpha(color, TREND_FILL_ALPHA) : color,
        borderWidth: trend ? 2.5 : 0,
        borderRadius: trend ? 0 : 4,
        maxBarThickness: 34,
        fill: trend,
        tension: LINE_TENSION,
        pointRadius: trend ? 2.5 : 0,
        pointHoverRadius: trend ? 5 : 0,
        pointBackgroundColor: color,
        pointBorderColor: surface,
        pointBorderWidth: 1.5
      } as ChartDataset;
    });

    return {
      type: trend ? "line" : "bar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 6 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: readToken(this.root, "--ink", "#211e19"),
            padding: 11,
            cornerRadius: 10,
            titleFont: { size: 12, weight: 600 },
            bodyFont: { size: 12 },
            bodySpacing: 5,
            usePointStyle: true,
            boxPadding: 4,
            callbacks: {
              footer: (items) => {
                const total = items.reduce((sum, item) => sum + Number(item.raw ?? 0), 0);
                return `Total outreach: ${total}`;
              }
            }
          }
        },
        scales: {
          x: {
            stacked: !trend,
            grid: { display: false },
            border: { color: gridline },
            ticks: { color: muted, font: { size: 11 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 18 }
          },
          y: {
            stacked: !trend,
            beginAtZero: true,
            border: { display: false },
            grid: { color: gridline },
            ticks: { color: muted, font: { size: 11 }, precision: 0, maxTicksLimit: 6, padding: 8 }
          }
        }
      }
    };
  }
}

let charts: CampaignOverviewChart[] = [];

export function initCampaignOverview(): void {
  charts = charts.filter((chart) => {
    if (!chart.isDetached()) return true;

    chart.destroy();
    return false;
  });

  document.querySelectorAll<HTMLElement>("[data-campaign-overview]").forEach((root) => {
    if (root.dataset.overviewReady === "true") return;

    root.dataset.overviewReady = "true";
    charts.push(new CampaignOverviewChart(root));
  });
}
